import {
  CognitoIdentityProviderClient,
  InitiateAuthCommand,
  InitiateAuthCommandInput,
} from '@aws-sdk/client-cognito-identity-provider';
import axiosStatic, { AxiosHeaders } from 'axios';
import { Dictionary } from 'lodash';
import { Optional } from 'utility-types';
import {
  AdvancedSetting,
  SettingType,
} from 'api/advanced-settings/settings-types';
import { Subscription } from 'api/billing/types';
import { ExpenseAccess } from 'api/expense-management/types';
import { AuditLog, CurrencyAuditLogMeta } from 'api/financials-audit-logs/type';
import { HolidayCalendar } from 'api/holiday-calendar/types';
import { HttpClient } from 'api/http-client';
import { ProjectPermission } from 'api/permissions';
import { PersonalTask } from 'api/personal-tasks';
import { ResourceManagementConfig } from 'api/resource-management/types';
import { RevenueConfig } from 'api/revenue-entries/revenue-config/types';
import { Field } from 'api/tasks';
import { ApiConfiguration } from 'api/timesheets/configuration/api-configuration';
import { AccountVisibility } from 'api/visibility';
import RocketlaneLogo from 'assets/rocketlane_logo_64px.png';
import { BaseCalendarConfig } from 'components/organisms/projects-report/common/range-picker/calendar-engine';
import {
  API_HOST_FOR_MOBILE_META,
  API_NAMESPACE,
  API_VERSION,
  updateApiHostForCookies,
} from 'config/environment';
import { AWS_COGNITO_CLIENT_REGION } from 'constant/constant';
import { InitWorkerRequestType, InitWorkerApi } from 'initWorker';
import { PermissionSet } from 'stores/permission-sets';
import { Company, DateType, OutOfOfficeConfig, User } from '../models';
import { ProjectInUserProfile, UserProfileInfo } from '../user';

declare global {
  interface Window {
    initWorker: Worker;
  }
}
const customDomainApi = '/accounts/custom-domain';

export enum TimesheetAccessState {
  Enabled = 'ENABLED',
  OnlyApproval = 'ONLY_APPROVAL',
  Disabled = 'DISABLED',
}

export interface BrandTheme {
  sideNavBg: string;
  sideNavText: string;
  brandColor: string;
  brandHoverColor: string;
  brandColorInRgb: string;
}

interface MetaData {
  theme?: BrandTheme;
  showProjectPlanBackground?: boolean;
  ftue?: boolean;
  canNotificationsConfigBeEdited?: boolean;
  // eslint-disable-next-line @typescript-eslint/naming-convention
  'ATTACHMENTS-maxFileSizeInBytes'?: number;
  userCount?: number;
  outOfOfficeConfig?: OutOfOfficeConfig;
}

export enum AccountStatus {
  Trial = 'TRIAL',
  Active = 'ACTIVE',
  Grace = 'GRACE',
  Suspended = 'SUSPENDED',
  Paid = 'PAID',
}

export enum WarningErrorCode {
  PaymentFailed = 'PAYMENT_FAILED',
}

export enum WarningType {
  Billing = 'BILLING',
}

export type Warning = {
  errorCode: WarningErrorCode;
  warningType: WarningType;
};

export interface Account {
  accountName: string;
  accountId: number;
  company: Company;
  metaData: Partial<MetaData>;
  primaryDomainName: string;
  defaultDomainName: string;
  expiryDate: string | null; // YYYY-MM-DD
  status: AccountStatus;
  timeZone: string;
  systemBotId: number;
  accountWarnings?: Warning[];
  createdAt: string | null;
}

export type UpdateAccountTimezonePayload = Pick<
  Account,
  'accountId' | 'timeZone'
>;

export type UpdateAccountPayload = {
  metaData?: MetaData;
  company?: Pick<Company, 'companyName'>;
};

export enum IdpType {
  Google = 'GOOGLE',
  Saml = 'SAML',
}

export enum AuthenticationServiceProvider {
  COGNITO = 'COGNITO',
  OKTA = 'OKTA',
}

export interface Idp {
  idpId: string;
  idpType: IdpType;
  enabled: boolean;
}

export interface OktaMetaData {
  clientId: string;
  issuerDomain: string;
  userName?: string;
  idps?: Idp[];
  authenticationServiceProvider: AuthenticationServiceProvider;
  customAuthentication: boolean;
  cookiesEnabled: boolean;
}

export interface AccountMeta {
  company: Company;
  okta: OktaMetaData;
  clientRegion?: string;
  features: {
    timesheets: boolean;
    financials: boolean;
    invoices: boolean;
    disablePoweredBy: boolean;
    disableCustomerSlackConnect: boolean;
    hippaCompliance: boolean;
    bulkPlaceHolderUpdate: boolean;
    aiFills: boolean;
    clickhouse: boolean;
    aiAgents: boolean;
    aiFillsStreaming: boolean;
    dashboardV2: boolean;
    taxes?: boolean;
    restrictUserGroups?: boolean;
    forcePrivateAttachments: boolean;
    multiLineBudgets?: boolean;
    emailIntelligence?: boolean;
    scarlet?: boolean;
    signalsAnalyst?: boolean;
    signals?: boolean;
    meetingPages?: boolean;
    emailPages?: boolean;
    documentationAgent?: boolean;
    newUI?: boolean;
    customReportsEmailExport?: boolean;
    nitroAnalyst?: boolean;
    nitroBilling?: boolean;
    timesheetPolicies?: boolean;
    knowledgeBase?: boolean;
    creditNotes?: boolean;
    migrationAgent?: boolean;
    policyEvaluation?: boolean;
    scheduledExports?: boolean;
    gtmCodeBlock?: boolean;
    salesforceIntegrationV2?: boolean;
    customReportsBudgetSourceTabular?: boolean;
    customReportsBudgetSourcePivot?: boolean;
  };
}

export interface BillingMeta {
  subscription: Omit<Subscription, 'subscriptionMeta'>;
  accountAdmin: User;
}

export interface DkimHost {
  host: string;
  data: string;
  verified: boolean;
}

export interface EmailConfigBase {
  emailDomain: string;
  invitationPrefix: string;
  notificationPrefix: string;
  enabled?: boolean;
}

export interface EmailConfig extends EmailConfigBase {
  domainName: string;
  spf: DkimHost;
  dkim1: DkimHost;
  dkim2: DkimHost;
  updatedAt?: string;
  createdAt?: string;
  verified: boolean;
  data?: DkimHost[]; // after transforming
}

export interface FtuePayload {
  enabled: boolean;
}

export interface CustomDomainHost {
  cnameType: 'SSL' | 'Domain';
  host: string;
  value: string;
}

export interface CustomDomain {
  domainName: string;
  cnames: CustomDomainHost[];
  primary: boolean;
  deleted: boolean;
}

export interface NotificationData {
  pendingNotifications?: number;
  newNotificationAvailable: boolean;
}

export interface AccountInitialData {
  accountMeta: AccountMeta;
  account: Account;
  accountConfig: AdvancedSetting[];
  userProfile: UserProfileInfo;
  companies: Company[];
  permissionSetList: PermissionSet[];
  accountVisibilitySettings: AccountVisibility;
  newNotificationAvailable: boolean;
  personalTasks: PersonalTask[];
  timeSheetAccess: TimesheetAccessState;
  billingMeta: BillingMeta;
  holidayCalendar: HolidayCalendar[];
  showG2: boolean;
  restrictUserGroups?: boolean;
  doesSprintsExist: boolean;
  newDependenciesCreated: boolean;
}

export interface AccountConfig {
  fields: Field[];
  timeSheetConfig: ApiConfiguration;
  revenueConfig: RevenueConfig;
  resourceManagementConfig: ResourceManagementConfig;
  projectsWithReadAccessForInvoices: number[];
  expenseAccess: ExpenseAccess;
}

export interface AccountInitDataProjectsQuery {
  projects: ProjectInUserProfile[];
  byId: Dictionary<ProjectInUserProfile>;
  projectPermissionBeanMap?: Record<number, ProjectPermission>;
}

export interface RawAccountInitialData
  extends Omit<AccountInitialData, 'userProfile'> {
  userProfile: Omit<AccountInitialData['userProfile'], 'permissions'> & {
    permissions: Omit<
      AccountInitialData['userProfile']['permissions'],
      'projects'
    > & { projects: ProjectPermission[] };
  };
}

export interface CreateCustomDomain {
  customDomainName: string;
}

export interface AccountSignUpPayload {
  emailId: string;
  companyName: string;
  domainName: string;
  firstName: string;
  lastName: string;
  newPassword: string;
  token: string;
  contactNumber: string;
  timeZone: string;
}

interface Session {
  sessionToken: string;
  sessionUser: User;
}
interface AccountWithDomain extends Account {
  primaryDomainName: string;
  defaultDomainName: string;
  expiryDate: string;
}
export interface AccountSignUpResponse {
  session: Session;
  account: AccountWithDomain;
  templateId: number;
}

export type IsDomainExistsPayload = {
  domainName: string;
  token: string;
  throughGoogleAuth: boolean;
};
export interface IsDomainExistsResponse extends IsDomainExistsPayload {
  domainExists: boolean;
}

export enum RequestPlanType {
  Buy = 'BUY',
  TrialExtend = 'TRIAL_EXTEND',
}

type ForgotAccountPayload = {
  email: string;
};

export type RequestPlanPayload = {
  type: RequestPlanType;
  metaData?: {
    plan: 'PREMIUM';
    noOfAgents: number;
  };
};

export interface Currency {
  currencyName: string;
  currencyCode: string;
  currencySymbol: string;
}

export interface AccountSignUpThroughGooglePayload {
  emailId: string;
  companyName: string;
  domainName: string;
  firstName: string;
  lastName: string;
  newPassword: string;
  contactNumber: string;
  timeZone: string;
  signUpHandlerType: string;
  credential: string;
}

export type CurrencyList = Currency[];

export interface CurrencyMapping {
  accountCurrencyMappingId: number;
  currency: Currency;
  exchangeRate: number;
  defaultCurrency: boolean;
  enabled: boolean;
  activeDefaultCurrency?: Currency;
  updatedAt?: DateType;
  updatedBy?: number;
  updatedByUser?: User;
  createdAt?: DateType;
  createdBy?: number;
  createdByUser?: User;
  backdatedTo?: DateType;
}

export type CurrencyMappingList = CurrencyMapping[];

export type UpdateCurrencyMappingPayload = Pick<
  CurrencyMapping,
  'accountCurrencyMappingId' | 'currency'
> &
  Optional<
    Pick<CurrencyMapping, 'enabled' | 'exchangeRate' | 'defaultCurrency'>,
    'enabled' | 'exchangeRate' | 'defaultCurrency'
  > & {
    backdate?: DateType;
  };

export interface CalendarConfigPayload {
  eventType: SettingType.CustomCalendarConfiguration;
  calendarConfig: BaseCalendarConfig;
  eventValue: boolean;
}

export interface CalendarConfig {
  calendarType: 'GREGORIAN' | 'RETAIL445';
  fiscalStartDate: string;
}

class AccountHttpClient extends HttpClient {
  public async getAccountDetails(): Promise<Account> {
    return this.get('/accounts/profile');
  }

  public getCurrencyList = async (): Promise<Currency[]> =>
    this.get('/accounts/currencies');

  public getCurrencyMappingList = async (): Promise<CurrencyMappingList> =>
    this.get('/accounts/currency-mapping');

  public addCurrencyMapping = async (payload: CurrencyMapping): Promise<void> =>
    this.post('/accounts/currency-mapping', payload);

  public async updateCurrencyMapping(
    payload: UpdateCurrencyMappingPayload,
  ): Promise<void> {
    return this.put(
      `/accounts/currency-mapping${
        payload.backdate != null ? `?backdate=${payload.backdate}` : ''
      }`,
      payload,
    );
  }

  public getCurrencyMappingHistory = async (
    currencyCode: Currency['currencyCode'],
  ): Promise<CurrencyMappingList> =>
    this.get(`/accounts/currency-mapping/history/${currencyCode}`);

  public getCurrencyMappingAuditLog = async (
    currencyCode: Currency['currencyCode'],
  ): Promise<AuditLog<CurrencyAuditLogMeta>[]> =>
    this.get(`/accounts/currency-mapping/audit-log/${currencyCode}`);

  public async updateAccount(data: UpdateAccountPayload): Promise<Account> {
    return this.put('/accounts/profile', data);
  }

  public async updateOutOfOffice(
    data: OutOfOfficeConfig,
  ): Promise<OutOfOfficeConfig> {
    return this.put('/accounts/profile/out-of-office', data);
  }

  public async updateTimezone(
    data: UpdateAccountTimezonePayload,
  ): Promise<Account> {
    return this.put('/accounts/profile', data);
  }

  public async updateCalendarConfig(
    payload: CalendarConfigPayload,
  ): Promise<void> {
    return this.post('/accounts/calendar-config', payload);
  }

  public async getMeta(
    domain: string | null,
    pathname: string | null,
  ): Promise<AccountMeta> {
    // if in localstorage, return that
    // else fetch from api and store in localstorage
    if (localStorage.getItem('accountMeta')) {
      return new Promise((resolve) => {
        const meta = localStorage.getItem('accountMeta');

        if (meta) {
          const parsed = JSON.parse(meta);

          if (parsed?.okta?.cookiesEnabled) {
            updateApiHostForCookies();
          }
          resolve(parsed);
        }
      });
    }

    if (
      navigator.userAgent.includes('Mobile') &&
      localStorage.getItem('customer_portal') !== 'true' &&
      !(
        pathname?.startsWith('/forms') ||
        pathname?.startsWith('/cp') ||
        pathname?.startsWith('/customer-portal')
      )
    ) {
      return axiosStatic
        .get<AccountMeta>(
          `${API_HOST_FOR_MOBILE_META}/${API_NAMESPACE}/${API_VERSION}/accounts/get-mobile-meta?domain=${domain}`,
          {
            headers: new AxiosHeaders({}),
          },
        )
        .then((x) => x.data)
        .then((response: AccountMeta): AccountMeta => {
          const { company } = response;
          const { companyLogoUrl } = company;

          return {
            ...response,
            company: {
              ...company,
              companyLogoUrl,
            },
            features: response.features ?? {},
          };
        });
    }

    let response: AccountMeta;

    const makeManualCall = async () => {
      return this.get<AccountMeta>('/accounts/get-meta', {
        headers: new AxiosHeaders({}),
      });
    };

    if (process.env.NODE_ENV === 'test') {
      response = await makeManualCall();

      return response;
    }

    try {
      response = (await InitWorkerApi.getData(
        InitWorkerRequestType.GetMeta,
      )) as AccountMeta;
    } catch {
      response = await makeManualCall();
    }

    // On every successful get-meta, update host when cookiesEnabled
    if (response?.okta?.cookiesEnabled) {
      updateApiHostForCookies();
    }

    const { company } = response;
    const companyLogoUrl = company.companyLogoUrl || RocketlaneLogo;

    const data = {
      ...response,
      company: {
        ...company,
        companyLogoUrl,
      },
      features: response.features ?? {},
    };

    return data;
  }

  public async getEmailConfig(): Promise<EmailConfig[]> {
    return this.get('/email/config');
  }

  public async addEmailConfig(
    data: Pick<EmailConfigBase, 'emailDomain' | 'enabled'>,
  ): Promise<EmailConfig> {
    return this.post('/email/config', data);
  }

  public async updateEmailConfig(
    data: Pick<EmailConfigBase, 'emailDomain' | 'enabled'>,
  ): Promise<EmailConfig> {
    return this.put('/email/config', data);
  }

  public async verifyEmailConfig(
    data: Pick<EmailConfigBase, 'emailDomain'>,
  ): Promise<EmailConfig> {
    return this.post('/email/verify', data);
  }

  public async toggleFTUE(payload: FtuePayload) {
    return this.put('/accounts/ftue', payload);
  }

  public getCustomDomain = (): Promise<CustomDomain[]> =>
    this.get<unknown, CustomDomain[]>(customDomainApi);

  public createCustomDomain = (
    payload: CreateCustomDomain,
  ): Promise<CustomDomain[]> =>
    this.post<CustomDomain[], CreateCustomDomain>(customDomainApi, payload);

  public verifyCustomDomain = (payload: CreateCustomDomain): Promise<boolean> =>
    this.post<boolean, CreateCustomDomain>(
      `${customDomainApi}/verify`,
      payload,
    );

  public getInitData = async (): Promise<AccountInitialData> => {
    let data: RawAccountInitialData;

    const makeManualCall = async () => {
      return this.get<RawAccountInitialData>('/home/init');
    };

    if (process.env.NODE_ENV === 'test') {
      data = await makeManualCall();

      return data;
    }

    try {
      data = (await InitWorkerApi.getData(
        InitWorkerRequestType.Init,
      )) as RawAccountInitialData;
    } catch {
      data = await makeManualCall();
    }

    return {
      ...data,
      userProfile: {
        ...data.userProfile,
        permissions: {
          ...data.userProfile.permissions,
          projects: data.userProfile.permissions.projectPermissionBeanMap ?? [],
        },
      },
    };
  };

  public getConfigData = async (): Promise<AccountConfig> => {
    let data: AccountConfig;

    const makeManualCall = async () => {
      return this.get<AccountConfig>('/home/configuration');
    };

    if (process.env.NODE_ENV === 'test') {
      data = await makeManualCall();

      return data;
    }
    try {
      data = (await InitWorkerApi.getData(
        InitWorkerRequestType.AccountConfig,
      )) as AccountConfig;
    } catch {
      data = await makeManualCall();
    }

    return data;
  };

  public signUp = ({
    token,
    ...payload
  }: AccountSignUpPayload): Promise<AccountSignUpResponse> => {
    return this.put('/accounts/sign-up', payload, { params: { token } });
  };

  public resendAccountActivation = () => {
    return this.post('/accounts/activate/resend');
  };

  public isDomainExists = ({
    token,
    ...payload
  }: IsDomainExistsPayload): Promise<IsDomainExistsResponse> => {
    if (payload.throughGoogleAuth) {
      return this.post(
        'https://admin.api.rocketlane.com/api/v1/accounts/domain',
        {
          domainName: payload.domainName,
        },
        {
          params: { token },
        },
      );
    }

    return this.post(
      '/accounts/domain',
      { domainName: payload.domainName },
      { params: { token } },
    );
  };

  public requestPlan = (payload: RequestPlanPayload) => {
    return this.put('/accounts/request-plan', payload);
  };

  public forgotAccount = (payload: ForgotAccountPayload) => {
    return this.post(
      'https://admin.api.rocketlane.com/api/v1/accounts/forgot',
      {
        emailId: payload.email,
      },
    );
  };

  public signUpThroughGoogle = ({
    ...payload
  }: AccountSignUpThroughGooglePayload): Promise<AccountSignUpResponse> => {
    return this.post(
      'https://admin.api.rocketlane.com/api/v1/accounts/signup-google',
      payload,
    );
  };

  public memberActivateResend = ({ email }: { email: string }) => {
    return this.post('/members/activate/resend', { email });
  };

  public logAccountMetricsToDataDog = (payload) =>
    this.post('/accounts/datadog/gauge', payload);

  // eslint-disable-next-line class-methods-use-this
  public loginWithCognitoSDK = async (payload) => {
    // Extract region from issuerDomain if available, otherwise fallback to default
    // issuerDomain format: https://<domain>.auth.<region>.amazoncognito.com
    let region = AWS_COGNITO_CLIENT_REGION; // default fallback

    if (payload.issuerDomain) {
      const regionMatch = payload.issuerDomain.match(
        /\.auth\.([^.]+)\.amazoncognito\.com/,
      );

      if (regionMatch) {
        const [, matchedRegion] = regionMatch;

        if (matchedRegion) {
          region = matchedRegion;
        }
      }
    }

    const awsCognitoClient = new CognitoIdentityProviderClient({
      region,
    });

    const userName = payload.userName.trim() + payload.userNamesuffix;

    const input: InitiateAuthCommandInput = {
      AuthFlow: 'USER_PASSWORD_AUTH',
      ClientId: payload.clientId,
      AuthParameters: {
        USERNAME: userName,
        PASSWORD: payload.password,
      },
    };

    const command = new InitiateAuthCommand(input);
    const { AuthenticationResult } = await awsCognitoClient.send(command);

    if (AuthenticationResult != null) {
      const { AccessToken } = AuthenticationResult;

      return { token: AccessToken };
    }

    return { token: null };
  };

  public loginWithCognitoCustomEndpoint = async (payload) => {
    const userName = payload.userName.trim() + payload.userNamesuffix;

    const response = await this.post('authentication/sign-in', {
      userName,
      password: payload.password,
      clientId: payload.clientId,
    });

    return response;
  };

  public loginWithCognito = async (payload, isCustomAuthentication) => {
    if (isCustomAuthentication) {
      return this.loginWithCognitoCustomEndpoint(payload);
    }

    return this.loginWithCognitoSDK(payload);
  };

  // eslint-disable-next-line class-methods-use-this
  public loginWithCognitoSSO = async (
    issuerDomain,
    code,
    redirectURI,
    codeVerifier,
    clientId,
  ) => {
    const queryParams = new URLSearchParams({
      grant_type: 'authorization_code',
      code,
      redirect_uri: redirectURI,
      code_verifier: codeVerifier,
      client_id: clientId,
    });

    try {
      const response = await axiosStatic.post(
        `${issuerDomain}/oauth2/token?${queryParams.toString()}`,
        {},
        {
          headers: {
            // eslint-disable-next-line @typescript-eslint/naming-convention
            'Content-Type': 'application/x-www-form-urlencoded',
          },
        },
      );

      return response.data;
    } catch (error: unknown) {
      const errorMessage =
        error instanceof Error ? error.message : 'Unknown error';

      throw new Error(`Login with Cognito SSO failed: ${errorMessage}`);
    }
  };
}

export const AccountAPI = new AccountHttpClient();
